
import React, { useState, useEffect } from 'react';
import { Check, X, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface Option {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface Question {
  id: string;
  text: string;
  type: 'single' | 'multiple' | 'true-false';
  options: Option[];
  explanation?: string;
}

interface QuizCardProps {
  question: Question;
  onNext: () => void;
  onAnswer: (correct: boolean) => void;
  isLast: boolean;
  onBackToTopics?: () => void;
}

const QuizCard: React.FC<QuizCardProps> = ({
  question,
  onNext,
  onAnswer,
  isLast,
  onBackToTopics
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [answered, setAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [cardAnimation, setCardAnimation] = useState('animate-scale-in');
  const { toast } = useToast();

  useEffect(() => {
    setSelectedOptions([]);
    setAnswered(false);
    setIsCorrect(false);
    setCardAnimation('animate-scale-in');
  }, [question]);

  const handleOptionClick = (optionId: string) => {
    if (answered) return;

    if (question.type === 'single' || question.type === 'true-false') {
      setSelectedOptions([optionId]);
    } else {
      // For multiple choice questions
      setSelectedOptions(prev => 
        prev.includes(optionId) 
          ? prev.filter(id => id !== optionId) 
          : [...prev, optionId]
      );
    }
  };

  const handleSubmit = () => {
    if (selectedOptions.length === 0) {
      toast({
        title: "Please select an answer",
        variant: "destructive",
      });
      return;
    }

    // For single or true-false questions
    if (question.type === 'single' || question.type === 'true-false') {
      const selected = question.options.find(opt => opt.id === selectedOptions[0]);
      setIsCorrect(selected?.isCorrect || false);
      setAnswered(true);
      onAnswer(selected?.isCorrect || false);
    } 
    // For multiple choice questions
    else {
      const allCorrectOptions = question.options.filter(opt => opt.isCorrect).map(opt => opt.id);
      const allCorrectSelected = allCorrectOptions.every(id => selectedOptions.includes(id));
      const noIncorrectSelected = selectedOptions.every(id => 
        question.options.find(opt => opt.id === id)?.isCorrect
      );
      
      const correct = allCorrectSelected && noIncorrectSelected;
      setIsCorrect(correct);
      setAnswered(true);
      onAnswer(correct);
    }
  };

  const handleNextQuestion = () => {
    setCardAnimation('animate-fade-out');
    
    setTimeout(() => {
      onNext();
    }, 300);
  };

  const isOptionSelected = (optionId: string) => selectedOptions.includes(optionId);

  const getOptionClass = (option: Option) => {
    if (!answered) {
      return isOptionSelected(option.id) 
        ? 'bg-accent text-accent-foreground' 
        : 'bg-card hover:bg-accent/50 text-card-foreground';
    }
    
    if (option.isCorrect) {
      return 'bg-teal-100 text-teal-800 border-teal-500';
    }
    
    if (isOptionSelected(option.id) && !option.isCorrect) {
      return 'bg-destructive/10 text-destructive border-destructive/50';
    }
    
    return 'bg-card text-card-foreground opacity-70';
  };

  return (
    <div className={cn(
      "w-full max-w-2xl mx-auto bg-white rounded-xl shadow-soft-lg p-6 md:p-8",
      cardAnimation
    )}>
      {onBackToTopics && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onBackToTopics}
          className="mb-4 -ml-2 text-muted-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to Topics
        </Button>
      )}
      
      <div className="mb-6">
        <h3 className="text-lg md:text-xl font-medium mb-2 text-balance">
          {question.text}
        </h3>
        <div className={cn(
          "text-xs uppercase font-semibold tracking-wider mb-4",
          question.type === 'multiple' ? "text-amber-600" : "text-primary"
        )}>
          {question.type === 'multiple' ? 'Select all that apply' : 'Select one answer'}
        </div>
      </div>

      <div className="space-y-2">
        {question.options.map((option) => (
          <div
            key={option.id}
            className={cn(
              "p-4 border rounded-lg cursor-pointer transition-all-soft flex justify-between items-center",
              getOptionClass(option)
            )}
            onClick={() => handleOptionClick(option.id)}
          >
            <div className="flex items-center">
              <div className={cn(
                "w-5 h-5 rounded-md border flex items-center justify-center mr-3 transition-all",
                question.type === 'multiple' 
                  ? 'rounded-md' 
                  : 'rounded-full',
                isOptionSelected(option.id) ? 'bg-primary border-primary' : 'bg-transparent'
              )}>
                {isOptionSelected(option.id) && (
                  <Check className="h-3 w-3 text-white" />
                )}
              </div>
              <span className={cn(
                "text-balance",
                option.text.includes('ال') || option.text.includes('في') ? 'text-arabic' : ''
              )}>
                {option.text}
              </span>
            </div>
            {answered && (
              <div className="ml-2">
                {option.isCorrect ? (
                  <Check className="h-5 w-5 text-teal-600" />
                ) : (
                  isOptionSelected(option.id) && <X className="h-5 w-5 text-destructive" />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!answered ? (
        <Button 
          className="w-full mt-6" 
          onClick={handleSubmit}
        >
          Submit Answer
        </Button>
      ) : (
        <>
          {question.explanation && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-1">Explanation:</h4>
              <p className="text-muted-foreground">{question.explanation}</p>
            </div>
          )}
          <Button 
            className="w-full mt-6" 
            onClick={handleNextQuestion}
          >
            {isLast ? "See Results" : "Next Question"}
          </Button>
        </>
      )}
    </div>
  );
};

export default QuizCard;
